import React, { useEffect, useState, useMemo, useRef } from 'react';
import HistoryItem from 'components/molecules/HistoryItem';
import { LoadingSpinner } from 'components/atoms';
import { useConversationList } from 'hooks/useConversationList';
import { useHistory } from 'context/HistoryContext';

export interface DocumentHistoryProps {
  userId: string;
  onDocumentClick?: (documentId: string, conversationId?: string) => void;
  className?: string;
  activeConversationId?: string | null;
}

const DocumentHistory: React.FC<DocumentHistoryProps> = ({
  userId,
  onDocumentClick,
  className = '',
  activeConversationId,
}) => {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const { registerRefetch } = useHistory();
  const hasRegisteredRef = useRef(false);

  const { conversations, loading, loadingMore, error, hasMore, refetch, loadMore } = useConversationList({
    userId,
    limit: 15,
    loadMoreSize: 10,
  });

  // Register refetch function with context (only once)
  useEffect(() => {
    if (!hasRegisteredRef.current) {
      registerRefetch(refetch);
      hasRegisteredRef.current = true;
    }
  }, [registerRefetch, refetch]);

  // Update selected conversation when activeConversationId changes
  useEffect(() => {
    if (activeConversationId) {
      setSelectedConversationId(activeConversationId);
    }
  }, [activeConversationId]);

  const handleConversationClick = React.useCallback(
    (conversationId: string) => {
      setSelectedConversationId(conversationId);
      if (onDocumentClick) {
        onDocumentClick(conversationId, conversationId);
      }
    },
    [onDocumentClick]
  );

  const conversationItems = useMemo(
    () =>
      conversations.map((conv) => (
        <HistoryItem
          key={conv.conversation_id}
          documentId={conv.conversation_id}
          conversationId={conv.conversation_id}
          filename={conv.title}
          status="completed"
          createdAt={conv.created_at}
          isActive={selectedConversationId === conv.conversation_id}
          onClick={handleConversationClick}
        />
      )),
    [conversations, selectedConversationId, handleConversationClick]
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 text-center ${className}`}>
        <p className="text-sm text-red-600">Failed to load history</p>
        <p className="text-xs text-red-500 mt-1">{error}</p>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className={`p-4 text-center ${className}`}>
        <p className="text-sm text-gray-500">No conversations yet</p>
        <p className="text-xs text-gray-400 mt-1">Start a conversation to get started</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="px-2 py-1">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Recent Conversations
        </h3>
      </div>
      <div className="flex flex-col gap-0.5 px-2">{conversationItems}</div>
      {hasMore && (
        <div className="px-2 pt-1 pb-2">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="w-full text-xs text-gray-500 hover:text-gray-700 py-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {loadingMore ? <LoadingSpinner size="sm" /> : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DocumentHistory;
